import { env } from '@/config/env';

import type { Assessment, Module, PendingTasks } from './types/modules-list';

export const openBrainGym = (roomId: string): boolean => {
  if (!roomId) return false;
  // window.open returns null when blocked (popup blocker / in-app webview);
  // return whether the tab actually opened so callers can detect failures.
  return Boolean(
    window.open(
      `${env.OLD_APP_URL}/quiz/practice/${roomId}?accessedBy=streamPlayer`,
      '_blank'
    )
  );
};

export const openAssessment = (assessmentId: string) => {
  if (!assessmentId) return;
  window.open(
    `${env.OLD_APP_URL}/assessment/v3/${assessmentId}/practice?accessedBy=streamPlayer`,
    '_blank'
  );
};

// dummy commit

const isModuleQuiz = (assessment: Assessment) =>
  assessment.assessmentCategoryType === 'MODULE' &&
  assessment.assessmentCategoryViewStatus === 'POST' &&
  Boolean(assessment.assessmentCategoryId);

// every variant of the same module quiz, this one included
const siblingsOf = (assessment: Assessment, assessments: Assessment[]) => {
  const siblings: Assessment[] = [];
  for (const other of assessments) {
    if (
      isModuleQuiz(other) &&
      other.assessmentCategoryId === assessment.assessmentCategoryId
    ) {
      siblings.push(other);
    }
  }
  return siblings;
};

// attempted wins, else the preferred one, else keep everything (the server sent no preference)
const shouldKeep = (assessment: Assessment, siblings: Assessment[]) => {
  let anyAttempted = false;
  let anyPreference = false;
  for (const sibling of siblings) {
    if (sibling.completionStatus) {
      anyAttempted = true;
    }
    if (typeof sibling.preferred === 'boolean') {
      anyPreference = true;
    }
  }
  if (anyAttempted) {
    return Boolean(assessment.completionStatus);
  }
  if (anyPreference) {
    return assessment.preferred === true;
  }
  return true;
};

/** Post-module quiz variants of one module: keep the attempted one, else the preferred one; without a server preference keep all. */
export const pickQuizVariants = (assessments: Assessment[]): Assessment[] => {
  const kept: Assessment[] = [];
  for (const assessment of assessments) {
    if (
      !isModuleQuiz(assessment) ||
      shouldKeep(assessment, siblingsOf(assessment, assessments))
    ) {
      kept.push(assessment);
    }
  }
  return kept;
};

export const pickPendingTaskVariants = (
  pendingTasks: PendingTasks
): PendingTasks => {
  if (!pendingTasks?.pendingAssessments) {
    return pendingTasks;
  }
  const pendingAssessments = pickQuizVariants(pendingTasks.pendingAssessments);
  return {
    ...pendingTasks,
    pendingAssessments,
    count: pendingAssessments.length,
  };
};

export const withPickedVariants = (module: Module): Module => ({
  ...module,
  pendingTasks: pickPendingTaskVariants(module.pendingTasks),
});
